// src/services/api.js
import axios from 'axios';
import { mutate as swrMutate } from 'swr';
import { auth } from './firebase';
import { clearTimezoneCache } from '../utils/dateConfig';
import { companyScopeHeaders, reportCompanyAccessFailure } from './companyDashboardScope';
import { APP_VERSION, DASHBOARD_VERSION_HEADER } from '../appVersion';
import { showDemoNotice, isDemoRefusal } from '../utils/demoNotice';
import { viewAsSession, isViewingAs, forgetViewAs, showViewAsReadOnlyNotice, VIEW_AS_HEADER, VIEW_AS_CODES } from '../utils/viewAs';
import { notifyDataSaved } from '../utils/dataSavedEvent';

// CRITICAL: API runs in chemical-monitor-api project (57459833175)
// Service name: chemical-monitor-api, Project: chemicalmonitoring
const API_URL = process.env.REACT_APP_API_URL || 'https://chemical-monitor-api-57459833175.us-central1.run.app/api';
export const API_BASE_URL = API_URL;

// Request deduplication - prevents duplicate in-flight requests
// If the same request is made while one is pending, return the same promise
const inFlightRequests = new Map();

// A transient endpoint-specific 401 must not turn a polling page into a
// request storm. After two consecutive unauthorized responses, pause that
// endpoint for a minute while leaving unrelated dashboard traffic alone.
const AUTH_BACKOFF_THRESHOLD = 2;
const AUTH_BACKOFF_MS = 60 * 1000;
const authBackoff = new Map();

function authBackoffKey(config) {
  const scope = config?._authBackoffScope || `${auth.currentUser?.uid || 'anonymous'}:${sessionStorage.getItem('sessionId') || 'no-session'}`;
  return `${scope}:${String(config?.method || 'get').toLowerCase()}:${String(config?.url || '').split('?')[0]}`;
}

function clearAuthBackoff(config) {
  if (config) authBackoff.delete(authBackoffKey(config));
}

function recordUnauthorized(config) {
  const key = authBackoffKey(config);
  const previous = authBackoff.get(key) || { consecutive401s: 0, blockedUntil: 0 };
  const consecutive401s = previous.consecutive401s + 1;
  const blockedUntil = consecutive401s >= AUTH_BACKOFF_THRESHOLD
    ? Date.now() + AUTH_BACKOFF_MS
    : previous.blockedUntil;
  authBackoff.set(key, { consecutive401s, blockedUntil });
}

function getAuthBackoffError(config) {
  const key = authBackoffKey(config);
  const state = authBackoff.get(key);
  if (!state || state.blockedUntil <= Date.now()) {
    if (state) authBackoff.delete(key);
    return null;
  }
  const error = new Error(`Authentication backoff active for ${config?.url || 'request'}`);
  error.code = 'AUTH_BACKOFF';
  error.config = config;
  error.authBackoffUntil = state.blockedUntil;
  return error;
}

function deduplicatedRequest(key, requestFn) {
  if (inFlightRequests.has(key)) {
    return inFlightRequests.get(key);
  }

  const promise = requestFn().finally(() => {
    inFlightRequests.delete(key);
  });

  inFlightRequests.set(key, promise);
  return promise;
}

// PERF2: everything that decides whose data a GET answers with (the login, the company being
// viewed, Developer View as, a support/developer selected account). Two requests only share one
// in-flight answer when all of these match, so a switch can never hand out another account's data.
function requestScopeKey() {
  const uid = auth.currentUser?.uid || 'anonymous';
  return [
    uid,
    companyScopeHeaders(uid)['x-company-org-id'] || '',
    viewAsSession()?.uid || '',
    sessionStorage.getItem('supportSelectedOrgId') || '',
    sessionStorage.getItem('developerSelectedOrgId') || '',
  ].join('|');
}

// GET that reuses an identical request still in flight for the same scope (PERF2: two effects
// asking for the same settings when a page opens now cost one round trip, not two).
export function sharedGet(url) {
  return deduplicatedRequest(`get:${requestScopeKey()}:${url}`, () => axiosInstance.get(url));
}

function refillOperationStorageKey(barrelId, gallons) {
  return `barrel-refill-operation:${barrelId}:${Number(gallons)}`;
}

function createRefillOperationId() {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  return `refill_${Date.now()}_${Math.random().toString(36).slice(2, 14)}`;
}

function getRefillOperationId(barrelId, gallons, suppliedOperationId) {
  if (suppliedOperationId) return suppliedOperationId;
  const storageKey = refillOperationStorageKey(barrelId, gallons);
  try {
    const existing = sessionStorage.getItem(storageKey);
    if (existing) return existing;
    const operationId = createRefillOperationId();
    sessionStorage.setItem(storageKey, operationId);
    return operationId;
  } catch {
    return createRefillOperationId();
  }
}

function levelAdjustmentOperationStorageKey(barrelId, level) {
  return `barrel-level-adjust-operation:${barrelId}:${Number(level)}`;
}

function createLevelAdjustmentOperationId() {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  return `adjust_${Date.now()}_${Math.random().toString(36).slice(2, 14)}`;
}

function getLevelAdjustmentOperationId(barrelId, level, suppliedOperationId) {
  if (suppliedOperationId) return suppliedOperationId;
  const storageKey = levelAdjustmentOperationStorageKey(barrelId, level);
  try {
    const existing = sessionStorage.getItem(storageKey);
    if (existing) return existing;
    const operationId = createLevelAdjustmentOperationId();
    sessionStorage.setItem(storageKey, operationId);
    return operationId;
  } catch {
    return createLevelAdjustmentOperationId();
  }
}

function normalizeLookbackMinutes(minutes, fallback = 60) {
  const numericMinutes = Number(minutes);
  if (!Number.isFinite(numericMinutes) || numericMinutes <= 0) {
    return fallback;
  }
  return Math.max(1, Math.ceil(numericMinutes));
}

// Create axios instance with default config
const axiosInstance = axios.create({
  baseURL: API_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  }
});

// Export axiosInstance for use in service modules
export default axiosInstance;

// Token helper: the signed-in login's Firebase ID token. (Developer View as sends the developer's own token plus the
// x-view-as-uid header; the old impersonation tokens are no longer used by the dashboard.)
export const getValidToken = async () => {
  try {
    const user = auth.currentUser;
    if (!user) {

      return null;
    }

    // Get token (Firebase handles refresh internally if needed)
    const token = await user.getIdToken(false); // false = don't force refresh

    return token;
  } catch (error) {
    console.error('[API] Error getting token:', error);
    return null;
  }
};

// Request interceptor to add token
axiosInstance.interceptors.request.use(
  async (config) => {
    config._authBackoffScope = `${auth.currentUser?.uid || 'anonymous'}:${sessionStorage.getItem('sessionId') || 'no-session'}`;
    const authBackoffError = getAuthBackoffError(config);
    if (authBackoffError) return Promise.reject(authBackoffError);

    config.headers[DASHBOARD_VERSION_HEADER] = APP_VERSION;
    const token = await getValidToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;

    } else {
      console.warn(`[API] No token available for: ${config.method} ${config.url}`);
    }

    if (!config.skipCompanyScope) {
      Object.assign(config.headers, companyScopeHeaders(auth.currentUser?.uid));
    }

    // Developer View as: every request answers as the viewed login, reads only (the API enforces it too).
    const viewing = config.skipViewAs ? null : viewAsSession();
    if (viewing) {
      if (!['get', 'head'].includes(String(config.method || 'get').toLowerCase())) {
        showViewAsReadOnlyNotice();
        const refused = new Error('View as is read-only. Nothing was changed.');
        refused.config = config;
        refused.response = { status: 403, data: { code: 'VIEW_AS_READ_ONLY', error: refused.message } };
        return Promise.reject(refused);
      }
      config.headers[VIEW_AS_HEADER] = viewing.uid;
    } else {
      // Add support org header if support user has selected an org
      const supportOrgId = sessionStorage.getItem('supportSelectedOrgId');
      if (supportOrgId) {
        config.headers['x-support-org-id'] = supportOrgId;
      }

      // Add developer org header if developer has selected an org
      const developerOrgId = sessionStorage.getItem('developerSelectedOrgId');
      if (developerOrgId) {
        config.headers['x-developer-org-id'] = developerOrgId;
      }
    }

    // Add session ID for session-limit enforcement
    const sessionId = sessionStorage.getItem('sessionId');
    if (sessionId) {
      config.headers['x-session-id'] = sessionId;
    }

    // Auto-inject selectedLocationId for location scoping
    // Callers may opt out for organization-wide operations; normal location scoping is unchanged.
    // Backend endpoints that don't need it simply ignore the param.
    // Check both config.params AND the URL string (some methods bake params via URLSearchParams).
    const selectedLocationId = localStorage.getItem('selectedLocationId');
    const hasLocationIdInParams = config.params?.locationId;
    const hasLocationIdInUrl = config.url?.includes('locationId=');
    if (!config.skipAutoLocation && selectedLocationId && !hasLocationIdInParams && !hasLocationIdInUrl) {
      config.params = config.params || {};
      config.params.locationId = selectedLocationId;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling and retry
axiosInstance.interceptors.response.use(
  response => {
    clearAuthBackoff(response.config);
    const method = String(response.config?.method || 'get').toLowerCase();
    if (method !== 'get') notifyDataSaved();
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Requests paused by endpoint backoff are expected and should not trigger
    // logging, company-access side effects, or another redirect.
    if (error.code === 'AUTH_BACKOFF') return Promise.reject(error);

    reportCompanyAccessFailure(error.response?.data);

    // Developer View as: a refused save gets one notice; a session that ended returns to the Developer Console.
    const viewAsCode = error.response?.status === 403 ? error.response?.data?.code : null;
    if (viewAsCode === 'VIEW_AS_READ_ONLY') {
      if (error.config) showViewAsReadOnlyNotice(error.response.data.error);
      return Promise.reject(error);
    }
    if (VIEW_AS_CODES.includes(viewAsCode) && isViewingAs()) {
      const sessionId = sessionStorage.getItem('sessionId'); // the developer's own session stays
      forgetViewAs();
      clearApiCache();
      if (sessionId) sessionStorage.setItem('sessionId', sessionId);
      window.location.assign('/developer');
      return Promise.reject(error);
    }

    // Public demo: a refused save gets one friendly notice (the page still sees the error).
    if (isDemoRefusal(error)) {
      showDemoNotice();
      return Promise.reject(error);
    }
    
    console.error('[API] Request failed:', {
      url: originalRequest?.url,
      status: error.response?.status,
      message: error.response?.data?.error || error.message
    });

    // Handle session limit - too many devices logged in with same account
    if (error.response?.status === 401 &&
        error.response?.data?.code === 'LOGGED_IN_ELSEWHERE') {


      // Clear all session data
      sessionStorage.clear();
      localStorage.removeItem('impersonation_session');

      // Show user-friendly message and redirect
      alert('You have been signed out because this account reached its active session limit.');
      window.location.href = '/login';
      return Promise.reject(error);
    }

    if (error.response?.status === 401) {
      recordUnauthorized(originalRequest);
    }

    // If token expired, try to refresh and retry
    if (error.response?.status === 401 && 
        (error.response?.data?.code === 'TOKEN_EXPIRED' || 
         error.response?.data?.error === 'Token expired') &&
        !originalRequest._retry) {
      

      originalRequest._retry = true;
      
      const user = auth.currentUser;
      if (user) {
        try {
          const newToken = await user.getIdToken(true); // true = force refresh
          originalRequest.headers.Authorization = `Bearer ${newToken}`;

          return axiosInstance(originalRequest);
        } catch (refreshError) {
          console.error('[API] Token refresh failed:', refreshError);
          // Redirect to login
          window.location.href = '/login';
        }
      }
    }
    
    // Do not redirect for an ambiguous 401. The endpoint-specific backoff
    // prevents polling storms while a transient token/middleware issue clears;
    // only explicit eviction or a failed TOKEN_EXPIRED refresh is terminal.
    
    return Promise.reject(error);
  }
);

// Cache configuration
const cache = new Map();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

// Get current user UID for cache key scoping (prevents cross-user data leaks)
const getCurrentUserUid = () => {
  return auth.currentUser?.uid || 'anonymous';
};

// Create user-scoped cache key
const getUserScopedKey = (key) => {
  return `${getCurrentUserUid()}:${key}`;
};

const getCachedData = (key) => {
  const scopedKey = getUserScopedKey(key);
  const cached = cache.get(scopedKey);
  if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
    return cached.data;
  }
  return null;
};

const setCachedData = (key, data) => {
  const scopedKey = getUserScopedKey(key);
  cache.set(scopedKey, { data, timestamp: Date.now() });
};

// Clear all cached data (call on logout/org switch/user change)
export const clearApiCache = () => {
  cache.clear();


  // Clear SWR's global cache so all pages refetch on org switch
  swrMutate(() => true, undefined, { revalidate: false });


  // Clear timezone cache (module-level cache in dateConfig.js)
  clearTimezoneCache();


  // Clear ALL app-related localStorage items that could leak between users
  const localStorageKeysToRemove = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && (
      key === 'impersonation_session' ||
      key === 'setupWizardStep' ||
      key.startsWith('sensorMappings') ||
      key.startsWith('api_cache') ||
      key.startsWith('settings_') ||
      key.startsWith('bayConfig') ||
      key.startsWith('chemical_') ||
      key.startsWith('wash_') ||
      key.startsWith('barrel_') ||
      key.startsWith('barrel-order') ||  // Note: uses hyphen, not underscore
      key.startsWith('facility_') ||
      key.startsWith('orderChem')  // Includes orderChemIgnoredChemicals
    )) {
      localStorageKeysToRemove.push(key);
    }
  }
  localStorageKeysToRemove.forEach(key => {

    localStorage.removeItem(key);
  });

  // Clear sessionStorage items (except keys needed for org selection)
  const sessionStorageKeysToPreserve = [
    'viewAsSession',
    'developerSelectedOrgId',
    'supportSelectedOrgId'
  ];
  const sessionStorageKeysToRemove = [];
  for (let i = 0; i < sessionStorage.length; i++) {
    const key = sessionStorage.key(i);
    if (key && !sessionStorageKeysToPreserve.includes(key)) {
      sessionStorageKeysToRemove.push(key);
    }
  }
  sessionStorageKeysToRemove.forEach(key => {

    sessionStorage.removeItem(key);
  });


};

// Callbacks registered by other modules to be notified when settings cache is cleared
// This avoids circular dependencies (useSettings.js imports from api.js, not vice versa)
const settingsCacheCallbacks = [];
export const onSettingsCacheCleared = (callback) => {
  settingsCacheCallbacks.push(callback);
};

// Clear all settings cache entries (for any location)
export const clearSettingsCache = () => {
  const uid = getCurrentUserUid();
  for (const key of cache.keys()) {
    if (key.startsWith(`${uid}:settings`)) {
      cache.delete(key);
    }
  }
  // Notify registered listeners (e.g., useSettings hook's module-level cache)
  settingsCacheCallbacks.forEach(cb => cb());

};

// Clear all sensor cache entries (for any location)
export const clearSensorCache = () => {
  const uid = getCurrentUserUid();
  for (const key of cache.keys()) {
    if (key.startsWith(`${uid}:sensors`)) {
      cache.delete(key);
    }
  }

};

// Helper function to normalize event data from the API
const normalizeEvent = (event) => {
  const timestamp = event.time ? new Date(event.time).getTime() :
                   event._time ? new Date(event._time).getTime() : 
                   event.timestamp ? new Date(event.timestamp).getTime() :
                   event.start_ms || Date.now();
  
  return {
    chip: event.chip || event.device_id || '',
    sensor: event.sensor || '',
    chemical: event.chemical || event.chemical_name || '',
    bay: event.bay || '',
    timestamp: timestamp,
    time: new Date(timestamp).toISOString(),
    volume: event.volume || event.volume_ml || 0,
    duration: event.duration || (event.duration_ms ? event.duration_ms / 1000 : 0),
    flow_rate: event.flow_rate || event.flowRate || event.flow_rate_ml_per_min || 0,
    ramp_up_time: event.ramp_up_time || 0,
    ramp_down_time: event.ramp_down_time || 0,
    steady_time: event.steady_time || 0,
    ramp_up_usage: event.ramp_up_usage || 0,
    ramp_down_usage: event.ramp_down_usage || 0,
    between_usage: event.between_usage || event.between_usage_ml || 0,
    leakage: event.leakage || 0,
    eventId: event.eventId || `${event.chip}_${event.sensor}_${timestamp}`
  };
};

// API methods object
export const api = {
  createActivity: async (data) => {
    const response = await axiosInstance.post('/audit-logs', data, { params: { locationId: data.locationId } });
    return response.data;
  },
  getHelpVideos: async (limit = 12) => {
    const response = await axiosInstance.get('/help-videos', {
      params: { limit }
    });
    return response.data;
  },

  // Sensor endpoints
  getSensors: async (locationId = null) => {
    const selectedLocationId = typeof window !== 'undefined'
      ? localStorage.getItem('selectedLocationId')
      : null;
    const effectiveLocationId = locationId || selectedLocationId || null;
    const cacheKey = effectiveLocationId ? `sensors:${effectiveLocationId}` : 'sensors';
    const cached = getCachedData(cacheKey);
    if (cached) return cached;

    const params = effectiveLocationId ? { locationId: effectiveLocationId } : {};
    // PERF2: pages and SensorContext ask at the same moment on page load; share one request.
    return deduplicatedRequest(`getSensors:${requestScopeKey()}:${cacheKey}`, async () => {
      const response = await axiosInstance.get('/sensors', { params });
      const data = response.data.sensors || [];
      setCachedData(cacheKey, data);
      return data;
    });
  },

  updateSensor: async (chipId, sensorId, data) => {
    const response = await axiosInstance.put(`/sensors/${chipId}/${sensorId}`, data);
    // Invalidate ALL sensor caches (both generic and location-specific)
    for (const key of cache.keys()) {
      if (key.includes(':sensors') || key.startsWith('sensors')) {
        cache.delete(key);
      }
    }
    return response.data;
  },

  moveChipBay: async (chipId, { oldBay, newBay, locationId }) => {
    const response = await axiosInstance.put(`/sensors/${chipId}/move-bay`, { oldBay, newBay, locationId });
    for (const key of cache.keys()) {
      if (key.includes(':sensors') || key.startsWith('sensors')) {
        cache.delete(key);
      }
    }
    return response.data;
  },

  resetAllSensors: async (chipId, { locationId }) => {
    const response = await axiosInstance.post(`/sensors/${chipId}/reset-all`, { locationId });
    for (const key of cache.keys()) {
      if (key.includes(':sensors') || key.startsWith('sensors')) {
        cache.delete(key);
      }
    }
    return response.data;
  },

  deleteSensor: async (chipId, sensorId) => {
    const response = await axiosInstance.delete(`/sensors/${chipId}/${sensorId}`);
    // Invalidate ALL sensor caches (both generic and location-specific)
    for (const key of cache.keys()) {
      if (key.includes(':sensors') || key.startsWith('sensors')) {
        cache.delete(key);
      }
    }
    return response.data;
  },

  // REMOVED 2025-12-07 (Revision 00305): initializeSensors, exportSensors
  // See docs/REVISION-00305-SYSTEM-SETUP-REMOVAL.md
  // Sensors are now created via Chip Inventory workflow

  // Alert endpoints
  // NOTE: getAlertHistory removed Dec 7, 2025 - alert_history collection deleted

  getAlertStatus: async () => {
    const response = await axiosInstance.get('/alerts/status');
    return response.data;
  },

  getAlertUsers: async () => {
    const response = await axiosInstance.get('/alerts/users');
    return response.data;
  },

  createAlertUser: async (data) => {
    const response = await axiosInstance.post('/alerts/users', data);
    return response.data;
  },

  updateAlertUser: async (userId, data) => {
    const response = await axiosInstance.put(`/alerts/users/${userId}`, data);
    return response.data;
  },

  deleteAlertUser: async (userId) => {
    const response = await axiosInstance.delete(`/alerts/users/${userId}`);
    return response.data;
  },

  getAlertRules: async () => {
    const response = await axiosInstance.get('/alerts/rules');
    return response.data;
  },

  updateAlertRule: async (ruleId, data) => {
    const response = await axiosInstance.put(`/alerts/rules/${ruleId}`, data);
    return response.data;
  },

  deleteAlertRule: async (ruleId) => {
    const response = await axiosInstance.delete(`/alerts/rules/${ruleId}`);
    return response.data;
  },




 // User management endpoints - ADD HERE
  addUser: async (userData) => {
    const response = await axiosInstance.post('/users/add', userData);
    return response.data;
  },

  getUsers: async () => {
    const response = await axiosInstance.get('/alerts/users');
    return response.data;
  },

  getOrganizationUsers: async () => {
    const response = await axiosInstance.get('/users/organization');
    return response.data;
  },

  deleteUser: async (uid) => {
    const response = await axiosInstance.delete(`/users/${uid}`);
    return response.data;
  },










  testAlert: async (data) => {
    const response = await axiosInstance.post('/alerts/test', data);
    return response.data;
  },

  // Event endpoints
  getLastNEvents: async (n) => {
    try {
      const response = await axiosInstance.get(`/events/last/${n}`);
      const rawEvents = response.data.events || [];

      // Normalize all events to ensure consistent field names
      const normalizedEvents = rawEvents.map(normalizeEvent);
      
      return normalizedEvents;
    } catch (error) {
      console.error('Error fetching last N events:', error);
      return [];
    }
  },

  getRecentEventsSnapshot: async (hours = 24, filters = {}) => {
    const params = new URLSearchParams();
    params.append('hours', String(hours));
    if (filters.locationId) params.append('locationId', filters.locationId);
    if (filters.orgId) params.append('orgId', filters.orgId);
    if (filters.forceRefresh) params.append('forceRefresh', 'true');
    if (Number.isFinite(filters.sinceMs)) params.append('sinceMs', String(Math.floor(filters.sinceMs)));
    const url = `/events/recent?${params.toString()}`;

    const response = await deduplicatedRequest(url, () => axiosInstance.get(url));
    const rawEvents = response?.data?.events;
    if (!Array.isArray(rawEvents)) {
      throw new Error('Invalid recent events response');
    }

    // Normalize all events to ensure consistent field names
    const normalizedEvents = rawEvents.map(normalizeEvent);

    // Sort by timestamp (newest first)
    normalizedEvents.sort((a, b) => b.timestamp - a.timestamp);

    return {
      events: normalizedEvents,
      // Older backend deployments may only return { events }; a client-side
      // receipt timestamp still marks the response as a confirmed success.
      generatedAt: response.data.generatedAt || new Date().toISOString(),
      count: Number.isFinite(response.data.count) ? response.data.count : normalizedEvents.length,
      query: response.data.query || { hours }
    };
  },

  getRecentEvents: async (hours = 24, filters = {}) => {
    const snapshot = await api.getRecentEventsSnapshot(hours, filters);
    return snapshot.events;
  },

  downloadEventsCsv: async ({
    hours,
    rangeKey,
    metric = 'all',
    bay = 'all',
    injectors = [],
    timezone = 'UTC',
    orgId,
    locationId,
    signal
  }) => {
    const params = new URLSearchParams();
    params.append('hours', String(hours));
    params.append('rangeKey', rangeKey);
    params.append('metric', metric);
    params.append('bay', bay);
    params.append('timezone', timezone);
    if (orgId) params.append('orgId', orgId);
    if (locationId) params.append('locationId', locationId);
    injectors.forEach(injector => params.append('injector', injector));

    try {
      const response = await axiosInstance.get(`/events/export.csv?${params.toString()}`, {
        responseType: 'blob',
        timeout: 5 * 60 * 1000,
        signal
      });
      const disposition = response.headers?.['content-disposition'] || '';
      const filenameMatch = disposition.match(/filename="([^"]+)"/i);
      const metricName = metric === 'all' ? 'all_metrics' : metric;
      return {
        blob: response.data,
        filename: filenameMatch?.[1] || `chemical_usage_${metricName}_${rangeKey}_${new Date().toISOString().slice(0, 10)}.csv`
      };
    } catch (error) {
      if (error.response?.data instanceof Blob && error.response.data.type.includes('json')) {
        try {
          const payload = JSON.parse(await error.response.data.text());
          error.userMessage = payload.error;
        } catch {
          // Keep the original network error when the response is not valid JSON.
        }
      }
      throw error;
    }
  },

  // Get events filtered by sensor (chip + sensor ID)
  getEventsBySensor: async (chipId, sensorId, limit = 100) => {
    try {
      const response = await axiosInstance.get(`/events/sensor/${chipId}/${sensorId}?limit=${limit}`);
      const rawEvents = response.data.events || [];
      
      const normalizedEvents = rawEvents.map(normalizeEvent);
      normalizedEvents.sort((a, b) => b.timestamp - a.timestamp);
      
      return normalizedEvents;
    } catch (error) {
      console.error('Error fetching events by sensor:', error);
      // Fallback to getting all events and filtering client-side
      const allEvents = await api.getLastNEvents(limit * 2);
      return allEvents.filter(e => 
        e.chip === chipId && 
        e.sensor === sensorId
      ).slice(0, limit);
    }
  },

  // Get events filtered by bay and chemical
  getEventsByChemical: async (bay, chemical, limit = 100) => {
    try {
      const response = await axiosInstance.get(`/events/chemical`, {
        params: { bay, chemical, limit }
      });
      const rawEvents = response.data.events || [];
      
      const normalizedEvents = rawEvents.map(normalizeEvent);
      normalizedEvents.sort((a, b) => b.timestamp - a.timestamp);
      
      return normalizedEvents;
    } catch (error) {
      console.error('Error fetching events by chemical:', error);
      // Fallback to getting all events and filtering client-side
      const allEvents = await api.getLastNEvents(limit * 5);
      const normalizedBay = bay.toLowerCase().replace(/\s+/g, '');
      const cleanChemical = chemical.replace(/^\d+\.\s*/, '').toLowerCase();
      
      return allEvents.filter(e => {
        const eventBay = (e.bay || '').toLowerCase().replace(/\s+/g, '');
        const eventChemical = (e.chemical || '').replace(/^\d+\.\s*/, '').toLowerCase();
        return eventBay === normalizedBay && eventChemical === cleanChemical;
      }).slice(0, limit);
    }
  },

  // Get recent events filtered by bay and chemical (time-based)
  getChemicalEventsRecent: async (bay, chemical, hours = 24, filters = {}) => {
    try {
      const response = await axiosInstance.get(`/events/chemical/recent`, {
        params: {
          bay,
          chemical,
          hours,
          ...(filters.locationId ? { locationId: filters.locationId } : {}),
          ...(filters.orgId ? { orgId: filters.orgId } : {})
        }
      });
      const rawEvents = response.data.events || [];
      
      const normalizedEvents = rawEvents.map(normalizeEvent);
      normalizedEvents.sort((a, b) => b.timestamp - a.timestamp);
      
      return normalizedEvents;
    } catch (error) {
      console.error('Error fetching recent chemical events:', error);
      // Fallback to getting all recent events and filtering client-side
      const allEvents = await api.getRecentEvents(hours, filters);
      const normalizedBay = bay.toLowerCase().replace(/\s+/g, '');
      const cleanChemical = chemical.replace(/^\d+\.\s*/, '').toLowerCase();
      
      return allEvents.filter(e => {
        const eventBay = (e.bay || '').toLowerCase().replace(/\s+/g, '');
        const eventChemical = (e.chemical || '').replace(/^\d+\.\s*/, '').toLowerCase();
        return eventBay === normalizedBay && eventChemical === cleanChemical;
      });
    }
  },

  // Pulse buffer endpoint
  getPulseBuffer: async () => {
    const response = await axiosInstance.get('/pulses/buffer');
    return response.data;
  },

  getRawPulseSensorData: async ({ chip, sensor, startDate, endDate, locationId }) => {
    const params = new URLSearchParams();
    params.append('chip', chip);
    params.append('sensor', sensor);
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    if (locationId) params.append('locationId', locationId);
    const response = await axiosInstance.get(`/raw-pulses/sensor?${params.toString()}`);
    return response.data;
  },

  // Dashboard summary endpoint - quick stats for landing page
  getDashboardSummary: async (locationId = null) => {
    const params = new URLSearchParams();
    if (locationId) params.append('locationId', locationId);
    const queryString = params.toString();
    const url = queryString ? `/dashboard/summary?${queryString}` : '/dashboard/summary';
    const response = await axiosInstance.get(url);
    return response.data;
  },

  // Dashboard AI insights endpoint - analyzes yesterday's usage patterns
  getDashboardInsights: async (locationId = null) => {
    const params = new URLSearchParams();
    if (locationId) params.append('locationId', locationId);
    const queryString = params.toString();
    const url = queryString ? `/dashboard/insights?${queryString}` : '/dashboard/insights';
    const response = await axiosInstance.get(url);
    return response.data;
  },

  // Statistics endpoints
  getBulkStatistics: async (sampleSize = null, locationId = null) => {
    const params = new URLSearchParams();
    if (sampleSize) params.append('sampleSize', sampleSize);
    if (locationId) params.append('locationId', locationId);
    const queryString = params.toString();
    const url = queryString ? `/statistics/bulk?${queryString}` : '/statistics/bulk';
    const response = await axiosInstance.get(url);
    return response.data;
  },

  // Raw events endpoint - returns raw event data for client-side statistical analysis
  getRawStatisticsEvents: async (sampleSize = 100, locationId = null) => {
    const params = new URLSearchParams();
    params.append('sampleSize', sampleSize);
    if (locationId) params.append('locationId', locationId);
    const response = await axiosInstance.get(`/statistics/raw-events?${params.toString()}`);
    return response.data;
  },

  resetStatistics: async (level, bay = null, chemical = null, locationId = null) => {
    const body = { level };
    if (bay) body.bay = bay;
    if (chemical) body.chemical = chemical;
    if (locationId) body.locationId = locationId;

    const response = await axiosInstance.post('/statistics/reset', body);
    return response.data;
  },

  clearStatisticsCache: async () => {
    const response = await axiosInstance.post('/statistics/cache/clear', {});
    return response.data;
  },

  // Settings endpoints
  getFlowTargetBenchmarks: async (locationId) => {
    const response = await axiosInstance.get('/settings/flow-target-benchmarks', { params: { locationId } });
    return response.data;
  },

  getSettings: async (locationId = null) => {
    const cacheKey = locationId ? `settings:${locationId}` : 'settings';
    const cached = getCachedData(cacheKey);
    if (cached) return cached;

    const params = locationId ? { locationId } : {};
    return deduplicatedRequest(`getSettings:${cacheKey}`, async () => {
      const response = await axiosInstance.get('/settings', { params });
      setCachedData(cacheKey, response.data);
      return response.data;
    });
  },

  updateSettings: async (bay, chemical, settings) => {
    const response = await axiosInstance.put(`/settings/${bay}/${chemical}`, settings);
    clearSettingsCache(); // Invalidate all settings cache
    return response.data;
  },

  bulkUpdateSettings: async (updates, locationId = null) => {
    const body = { updates };
    if (locationId) body.locationId = locationId;  // Pass admin's selected location
    const response = await axiosInstance.post('/settings/bulk', body);
    clearSettingsCache(); // Invalidate all settings cache
    return response.data;
  },

  // Organization and Location endpoints
  getOrganizations: async () => {
    try {
      const response = await axiosInstance.get('/organizations');
      return response.data.organizations || response.data || [];
    } catch (error) {
      console.error('[API] Error fetching organizations:', error);
      return [];
    }
  },

  getLocationsByOrg: async (orgId) => {
    try {
      const response = await axiosInstance.get(`/developer/locations?orgId=${orgId}`);
      return response.data.locations || response.data || [];
    } catch (error) {
      console.error('[API] Error fetching locations:', error);
      return [];
    }
  },

  // Barrel endpoints
  getBarrelConfigs: async (locationId = null) => {
    const params = locationId ? { locationId } : {};
    const response = await axiosInstance.get('/barrels/configs', { params });

    // The backend returns { barrels: [...] }
    // But if for some reason we get an array directly, handle that too
    if (Array.isArray(response.data)) {
      return response.data;
    }

    const barrels = response.data.barrels || [];
    return barrels;
  },

createBarrelConfig: async (barrelId, data) => {
  const response = await axiosInstance.post(`/barrels/configs/${barrelId}`, data);
  return response.data;
},

updateBarrelConfig: async (barrelId, data) => {
  const response = await axiosInstance.post(`/barrels/configs/${barrelId}`, data);
  return response.data;
},

  deleteBarrelConfig: async (barrelId) => {
    const response = await axiosInstance.delete(`/barrels/configs/${barrelId}`);
    return response.data;
  },

  initializeBarrel: async (data) => {
    const response = await axiosInstance.post('/barrels/initialize', data);
    return response.data;
  },

  // AI diagnostic knowledge
  getAiKnowledge: async () => {
    const response = await axiosInstance.get('/ai-knowledge');
    return response.data;
  },

  saveAiKnowledge: async (rules) => {
    const response = await axiosInstance.put('/ai-knowledge', { rules });
    return response.data;
  },

  // Custom column groups
  getCustomGroups: async (locationId = null) => {
    const params = {};
    if (locationId) params.locationId = locationId;
    const response = await axiosInstance.get('/custom-groups', { params });
    return response.data;
  },

  saveCustomGroups: async (groups, locationId = null) => {
    const response = await axiosInstance.put('/custom-groups', { groups, locationId });
    return response.data;
  },

  refillBarrel: async (barrelId, data) => {
    const operationId = getRefillOperationId(barrelId, data?.gallons, data?.operationId);
    const requestKey = `barrel-refill:${barrelId}:${operationId}`;
    return deduplicatedRequest(requestKey, async () => {
      const response = await axiosInstance.post(`/barrels/${barrelId}/refill`, {
        ...data,
        operationId
      });
      try {
        sessionStorage.removeItem(refillOperationStorageKey(barrelId, data?.gallons));
      } catch {
        // The backend operation ID still protects retries when storage is unavailable.
      }
      return response.data;
    });
  },

  adjustBarrelLevel: async (barrelId, data) => {
    const operationId = getLevelAdjustmentOperationId(barrelId, data?.level, data?.operationId);
    const requestKey = `barrel-level-adjust:${barrelId}:${operationId}`;
    return deduplicatedRequest(requestKey, async () => {
      const response = await axiosInstance.post(`/barrels/${barrelId}/adjust-level`, {
        ...data,
        operationId
      });
      try {
        sessionStorage.removeItem(levelAdjustmentOperationStorageKey(barrelId, data?.level));
      } catch {
        // The backend operation ID still protects retries when storage is unavailable.
      }
      return response.data;
    });
  },

getFacilityInventory: async (locationId = null) => {
  const params = new URLSearchParams({ t: Date.now() });
  if (locationId) params.append('locationId', locationId);
  const response = await axiosInstance.get(`/facility-inventory?${params.toString()}`);
  return response.data;
},

  purchaseFacilityInventory: async (data) => {
    const response = await axiosInstance.post('/facility-inventory/purchase', data);
    return response.data;
  },


  adjustInventory: async (data) => {
    const response = await axiosInstance.post('/facility-inventory/adjust', data);
    return response.data;
  },



  // Alias for FacilityInventoryPage compatibility
  recordPurchase: async (data) => {
    return api.purchaseFacilityInventory(data);
  },

  // Chemical rename and delete endpoints
  renameChemical: async (oldId, newId, locationId = null) => {
    const response = await axiosInstance.put(`/facility-inventory/${oldId}/rename`, {
      newChemicalId: newId,
      locationId
    });
    return response.data;
  },

  deleteChemical: async (chemicalId, locationId = null) => {
    const params = locationId ? { locationId } : {};
    const response = await axiosInstance.delete(`/facility-inventory/${chemicalId}`, { params });
    return response.data;
  },

  updateChemicalMetadata: async (chemicalId, metadata, locationId = null) => {
    const response = await axiosInstance.put(`/facility-inventory/${chemicalId}/metadata`, {
      ...metadata,
      locationId
    });
    return response.data;
  },

  saveProductDetails: async (data) => {
    const response = await axiosInstance.post('/product-details', data);
    return response.data;
  },

  // effectiveFrom: 'YYYY-MM-DD', or null for the since-the-beginning entry.
  deletePriceHistoryEntry: async (chemicalId, effectiveFrom, locationId = null) => {
    const params = locationId ? { locationId } : {};
    const response = await axiosInstance.delete(
      `/product-details/${encodeURIComponent(chemicalId)}/price-history/${effectiveFrom || 'baseline'}`,
      { params }
    );
    return response.data;
  },

  getProductDetails: async (chemicalId, locationId = null) => {
    const params = locationId ? { locationId } : {};
    const response = await axiosInstance.get(`/product-details/${encodeURIComponent(chemicalId)}`, { params });
    return response.data;
  },

  // Partner API access (Admin -> Integrations): which partners may read which locations
  getPartnerAccess: async (orgId = null) => {
    const response = await axiosInstance.get('/partner-access', { params: orgId ? { orgId } : {} });
    return response.data;
  },

  setPartnerAccess: async (clientId, locationId, enabled, orgId = null) => {
    const response = await axiosInstance.put(
      `/partner-access/${encodeURIComponent(clientId)}/locations/${encodeURIComponent(locationId)}`,
      { enabled },
      { params: orgId ? { orgId } : {} }
    );
    return response.data;
  },

  getAllProductDetails: async (locationId = null) => {
    const params = locationId ? { locationId } : {};
    const response = await axiosInstance.get('/product-details', { params });
    return response.data;
  },

  // Wash package endpoints
  getWashPackages: async (locationId = null) => {
    const params = locationId ? { locationId } : {};
    const response = await axiosInstance.get('/wash-packages', { params });
    return response.data;
  },

  updateWashPackage: async (packageId, data, locationId = null) => {
    const params = locationId ? { locationId } : {};
    const response = await axiosInstance.put(`/wash-packages/${packageId}`, data, { params });
    return response.data;
  },

  deleteWashPackage: async (packageId, locationId = null) => {
    const params = locationId ? { locationId } : {};
    const response = await axiosInstance.delete(`/wash-packages/${packageId}`, { params });
    return response.data;
  },

  getWashDetectionRecent: async (bay, minutes, filters = {}) => {
    const safeMinutes = normalizeLookbackMinutes(minutes);
    const params = new URLSearchParams({ minutes: safeMinutes.toString() });
    if (filters.orgId) params.append('orgId', filters.orgId);
    if (filters.locationId) params.append('locationId', filters.locationId);
    if (filters.forceRefresh) params.append('forceRefresh', 'true');
    const response = await axiosInstance.get(`/wash-detection/recent/${bay}?${params.toString()}`);
    return response.data;
  },

  // PERFORMANCE: Fetch all bays in one request instead of 3-4 separate calls
  // Uses deduplication to prevent duplicate in-flight requests
  // Per-day facts for a local date range (stored nightly days + today live).
  // docs/2026-09-26-DATA-PIPELINE-REVIEW.md
  getWashRangeSummary: async (startDate, endDate, filters = {}) => {
    const params = new URLSearchParams({ startDate, endDate });
    if (filters.orgId) params.append('orgId', filters.orgId);
    if (filters.locationId) params.append('locationId', filters.locationId);
    const response = await axiosInstance.get(`/wash-detection/range-summary?${params.toString()}`);
    return response.data;
  },

  // Individual washes for whole local days (recent-all response shape) from stored
  // per-wash detail + today live; max 62 days. docs/2026-09-26-DATA-PIPELINE-REVIEW.md
  getWashRangeWashes: async (startDate, endDate, filters = {}) => {
    const params = new URLSearchParams({ startDate, endDate });
    if (filters.orgId) params.append('orgId', filters.orgId);
    if (filters.locationId) params.append('locationId', filters.locationId);
    const response = await axiosInstance.get(`/wash-detection/range-washes?${params.toString()}`);
    return response.data;
  },

  getWashDetectionRecentAll: async (minutes, filters = {}, options = {}) => {
    const safeMinutes = normalizeLookbackMinutes(minutes);
    const params = new URLSearchParams({ minutes: safeMinutes.toString() });
    if (filters.orgId) params.append('orgId', filters.orgId);
    if (filters.locationId) params.append('locationId', filters.locationId);
    if (options.forceRefresh) params.append('forceRefresh', 'true');
    const url = `/wash-detection/recent-all?${params.toString()}`;

    return deduplicatedRequest(url, async () => {
      const response = await axiosInstance.get(url, {
        timeout: options.timeout || undefined
      });
      return response.data;
    });
  },

  getWashDailySummaries: async (startDate, endDate) => {
    const params = new URLSearchParams({ startDate, endDate });
    const response = await axiosInstance.get(`/wash-detection/daily-summaries?${params.toString()}`);
    return response.data;
  },

  getWashActuals: async (startDate, endDate) => {
    const params = new URLSearchParams({ startDate, endDate });
    const response = await axiosInstance.get(`/wash-actuals?${params.toString()}`);
    return response.data;
  },

  saveWashActual: async (date, byPackage) => {
    const response = await axiosInstance.put(`/wash-actuals/${date}`, { by_package: byPackage });
    return response.data;
  },

  // Wash counts imported from outside systems (Dencar, counter sensors, ...)
  getWashCountSources: async () => {
    const response = await axiosInstance.get('/wash-count-sources');
    return response.data;
  },

  // Experimental Wash Replay (Mission Control): one day of washes from the controller I/O log.
  getWashReplay: async ({ source, date } = {}, locationId = null) => {
    const params = {};
    if (source) params.source = source;
    if (date) params.date = date;
    if (locationId) params.locationId = locationId;
    const response = await axiosInstance.get('/controller-io/wash-replay', { params });
    return response.data;
  },

  // Chemical Usage "Valve" column + missed dispenses (controller-reader sites only).
  // Bays at this location whose wash-controller reading is on ({bays: ['bay3'], demo?}).
  getControllerBays: async (locationId = null) => {
    const response = await axiosInstance.get('/controller-io/bays', { params: locationId ? { locationId } : {} });
    return response.data;
  },

  // Settings -> Wash Equipment Integration (docs/2026-10-23-WASH-EQUIPMENT-INTEGRATION-DESIGN.md).
  getWashEquipmentBays: async (locationId = null) => {
    const response = await axiosInstance.get('/wash-equipment/bays', { params: locationId ? { locationId } : {} });
    return response.data;
  },

  saveWashEquipmentBay: async (bay, data, locationId = null) => {
    const response = await axiosInstance.put(`/wash-equipment/bays/${encodeURIComponent(bay)}`, data,
      { params: locationId ? { locationId } : {} });
    return response.data;
  },

  // Developers: per sensor, the relay its flows start with vs the saved relay (last 3 days).
  getWashEquipmentRelayCheck: async (bay, locationId = null) => {
    const response = await axiosInstance.get(`/wash-equipment/bays/${encodeURIComponent(bay)}/relay-check`,
      { params: locationId ? { locationId } : {} });
    return response.data;
  },

  // {bays: {bay2: 'accutrac_360i', bay3: null}}: set or clear Petit equipment on those bays.
  setPetitBays: async (bays, locationId = null) => {
    const response = await axiosInstance.put('/wash-equipment/petit-bays', { bays }, { params: locationId ? { locationId } : {} });
    return response.data;
  },

  getValveTimes: async (hours, locationId = null) => {
    const params = { hours };
    if (locationId) params.locationId = locationId;
    const response = await axiosInstance.get('/controller-io/valve-times', { params });
    return response.data;
  },


  getWashCountImports: async (startDate, endDate) => {
    const params = new URLSearchParams({ startDate, endDate });
    const response = await axiosInstance.get(`/wash-count-imports?${params.toString()}`);
    return response.data;
  },

  runWashCountImport: async (startDate, endDate, types) => {
    const response = await axiosInstance.post('/wash-count-imports/run', { startDate, endDate, types }, { timeout: 180000 });
    return response.data;
  },

  compareDetectionMethods: async (minutes = 10080, locationId = null) => {
    const params = new URLSearchParams({ minutes: minutes.toString() });
    if (locationId) params.append('locationId', locationId);
    const response = await axiosInstance.get(`/wash-detection/compare?${params.toString()}`);
    return response.data;
  },

  testPackageDetection: async (minutes = 10080, locationId = null) => {
    const params = new URLSearchParams({ minutes: minutes.toString() });
    if (locationId) params.append('locationId', locationId);
    const response = await axiosInstance.get(`/wash-detection/test-packages?${params.toString()}`);
    return response.data;
  },

  getDetectionMethod: async () => {
    const response = await axiosInstance.get('/wash-detection/method');
    return response.data;
  },

  setDetectionMethod: async (method) => {
    const response = await axiosInstance.put('/wash-detection/method', { method });
    return response.data;
  },

  // Admin endpoints
  adminReload: async () => {
    const response = await axiosInstance.post('/admin/reload', {});
    return response.data;
  },

  getStartupInfo: async () => {
    const response = await axiosInstance.get('/startup-info');
    return response.data;
  },


updateUser: async (uid, userData) => {
  const response = await axiosInstance.put(`/users/${uid}`, userData);
  return response.data;
},



getAuditLogs: async (timeRange = '7d', locationId = null) => {
  const params = new URLSearchParams({ range: timeRange });
  if (locationId) params.append('locationId', locationId);
  const response = await axiosInstance.get(`/audit-logs?${params.toString()}`);
  return response.data;
},

updateAuditLogStatus: async (logId, status, notes = null, forwardedTo = null) => {
  const response = await axiosInstance.put(`/audit-logs/${logId}/status`, {
    status,
    notes,
    forwardedTo
  });
  return response.data;
},

getDeveloperActivityLogs: async (timeRange = '7d', actionFilter = 'all') => {
  const response = await axiosInstance.get(`/developer/activity-logs?range=${timeRange}&filter=${actionFilter}`);
  return response.data;
},


  // Yearly chemical usage summary (aggregated by month)
  getYearlyChemicalUsage: async (year, filters = {}) => {
    try {
      const params = new URLSearchParams();
      params.append('year', year);
      if (filters.locationId) params.append('locationId', filters.locationId);


      const response = await axiosInstance.get(`/usage/yearly-summary?${params.toString()}`);
      return response.data;
    } catch (error) {
      console.error('[API] Error fetching yearly chemical usage:', error);
      return { data: [], year, error: error.message };
    }
  },

  // Chemical nesting config (for combining renamed chemicals in Order Chem report)
  getChemicalNesting: async (locationId = null) => {
    try {
      const params = new URLSearchParams();
      if (locationId) params.append('locationId', locationId);
      const response = await axiosInstance.get(`/usage/chemical-nesting?${params.toString()}`);
      return response.data;
    } catch (error) {
      console.error('[API] Error fetching chemical nesting:', error);
      return { nestings: {} };
    }
  },

  saveChemicalNesting: async (nestings, locationId = null) => {
    const params = new URLSearchParams();
    if (locationId) params.append('locationId', locationId);
    const response = await axiosInstance.put(`/usage/chemical-nesting?${params.toString()}`, { nestings });
    return response.data;
  },

  // Desired months settings (Order Chem page)
  getDesiredMonths: async () => {
    try {
      const response = await axiosInstance.get('/settings/desired-months');
      return response.data.desiredMonths || {};
    } catch (error) {
      console.error('[API] Error fetching desired months:', error);
      return {};
    }
  },

  saveDesiredMonths: async (desiredMonths) => {
    const response = await axiosInstance.put('/settings/desired-months', { desiredMonths });
    return response.data;
  },

  // Gallons per case settings (Order Chem page)
  getGalPerCase: async () => {
    try {
      const response = await axiosInstance.get('/settings/gal-per-case');
      return response.data.galPerCase || {};
    } catch (error) {
      console.error('[API] Error fetching gal per case:', error);
      return {};
    }
  },

  saveGalPerCase: async (galPerCase) => {
    const response = await axiosInstance.put('/settings/gal-per-case', { galPerCase });
    return response.data;
  },

  // Desired washes settings (Order Chem page - wash count mode)
  getDesiredWashes: async () => {
    try {
      const response = await axiosInstance.get('/settings/desired-washes');
      return response.data.desiredWashes || {};
    } catch (error) {
      console.error('[API] Error fetching desired washes:', error);
      return {};
    }
  },

  saveDesiredWashes: async (desiredWashes) => {
    const response = await axiosInstance.put('/settings/desired-washes', { desiredWashes });
    return response.data;
  },

  // Order mode setting (gallons vs washes)
  getOrderMode: async () => {
    try {
      const response = await axiosInstance.get('/settings/order-mode');
      return response.data.orderMode || 'washes';
    } catch (error) {
      console.error('[API] Error fetching order mode:', error);
      return 'washes';
    }
  },

  saveOrderMode: async (orderMode) => {
    const response = await axiosInstance.put('/settings/order-mode', { orderMode });
    return response.data;
  },

  // Washes for Order Period (Order Chem page, washes mode) - org-wide, 0 = not set
  getWashesForPeriod: async () => {
    const response = await axiosInstance.get('/settings/washes-for-period');
    return response.data.washesForPeriod || 0;
  },

  saveWashesForPeriod: async (washesForPeriod) => {
    const response = await axiosInstance.put('/settings/washes-for-period', { washesForPeriod });
    return response.data;
  },

  // Cases to order settings (Order Chem page - user-entered order quantities)
  getCasesToOrder: async () => {
    try {
      const response = await axiosInstance.get('/settings/cases-to-order');
      return response.data.casesToOrder || {};
    } catch (error) {
      console.error('[API] Error fetching cases to order:', error);
      return {};
    }
  },

  markOrderReceived: async (orderId, receivedItems) => {
    const response = await axiosInstance.post(`/orders/${orderId}/receive`, { receivedItems });
    return response.data;
  },

  saveCasesToOrder: async (casesToOrder) => {
    const response = await axiosInstance.put('/settings/cases-to-order', { casesToOrder });
    return response.data;
  },

  // Send chemical order email
  sendOrderEmail: async ({ recipients, items, customMessage }) => {
    const response = await axiosInstance.post('/orders/send-email', { recipients, items, customMessage });
    return response.data;
  },

  scanOutliers: async (year, thresholdMl, locationId = null) => {
    const params = {};
    if (year) params.year = year;
    if (thresholdMl) params.thresholdMl = thresholdMl;
    if (locationId) params.locationId = locationId;
    const response = await axiosInstance.get('/data-cleanup/outliers', { params, timeout: 120000 });
    return response.data;
  },

  deleteOutlierEvent: async (rowKey) => {
    const response = await axiosInstance.delete('/data-cleanup/event', { data: { rowKey } });
    return response.data;
  },

  recomputeDailySummaries: async (startDate, endDate) => {
    const response = await axiosInstance.post('/data-cleanup/recompute-summaries', { startDate, endDate }, { timeout: 300000 });
    return response.data;
  },

  getOrderHistory: async (limit = 20) => {
    const response = await axiosInstance.get('/orders/history', { params: { limit } });
    return response.data;
  },

  // Health check
  healthCheck: async () => {
    const response = await axiosInstance.get('/health');
    return response.data;
  },

  // Report Schedules
  getReportSchedules: async () => {
    const response = await axiosInstance.get('/report-schedules');
    return response.data;
  },

  createReportSchedule: async (schedule) => {
    const response = await axiosInstance.post('/report-schedules', schedule);
    return response.data;
  },

  updateReportSchedule: async (scheduleId, updates) => {
    const response = await axiosInstance.put(`/report-schedules/${scheduleId}`, updates);
    return response.data;
  },

  deleteReportSchedule: async (scheduleId) => {
    const response = await axiosInstance.delete(`/report-schedules/${scheduleId}`);
    return response.data;
  },

  testReportSchedule: async (scheduleId) => {
    // Use longer timeout for report generation
    const response = await axiosInstance.post(`/report-schedules/${scheduleId}/force-send`, {}, {
      timeout: 60000 // 60 seconds for report generation
    });
    return response.data;
  },

  // Activity Alert Management
  // SH8: company view - the company's own people who can receive alerts
  getAlertPeople: async () => {
    const response = await axiosInstance.get('/alert-users/people');
    return response.data;
  },

  // Every alert record the requester may see, at all sites (the page splits them by the location picked)
  getAllAlertUsers: async () => {
    const response = await axiosInstance.get('/alert-users', { skipAutoLocation: true });
    return response.data;
  },

  getActivityAlertUsers: async () => {
    const response = await axiosInstance.get('/alert-users');
    return response.data;
  },

  enableActivityAlerts: async (email, enabled = true, phone = null, carrier = null, preferences = null, locationId = null) => {
    const body = { email, enabled };

    if (phone) body.phone = phone;
    if (carrier) body.carrier = carrier;
    if (preferences) body.preferences = preferences;
    if (locationId) body.locationId = locationId;  // Pass admin's selected location

    const response = await axiosInstance.post('/alert-users/activity-alerts', body);
    return response.data;
  },

  testActivityAlert: async (email, method = 'email', alertType = null) => {
    const response = await axiosInstance.post('/alert-users/test-activity-alert', {
      email,
      method,
      alertType
    });
    return response.data;
  },

  // Threshold Alert Management
  getThresholdAlertUsers: async () => {
    const response = await axiosInstance.get('/alert-users');
    return response.data;
  },

  enableThresholdAlerts: async (email, enabled = true, phone = null, carrier = null, preferences = null, locationId = null) => {
    const body = { email, enabled };

    if (phone) body.phone = phone;
    if (carrier) body.carrier = carrier;
    if (preferences) body.preferences = preferences;
    if (locationId) body.locationId = locationId;  // Pass admin's selected location

    const response = await axiosInstance.post('/alert-users/threshold-alerts', body);
    return response.data;
  },

  saveAlertQuietHours: async (start, end, locationId = null) => {
    const body = { start, end };
    if (locationId) body.locationId = locationId;
    const response = await axiosInstance.post('/alert-users/quiet-hours', body);
    return response.data;
  },

  testThresholdAlert: async (email, alertType = 'low_inventory', method = null) => {
    const response = await axiosInstance.post('/alert-users/test-threshold-alert', {
      email,
      alertType,
      method
    });
    return response.data;
  },

  // Alert Thresholds (chemical usage baselines with min/max)
  getAlertThresholds: async (locationId = null) => {
    const params = new URLSearchParams();
    if (locationId) params.append('locationId', locationId);
    const response = await axiosInstance.get(`/alert-thresholds?${params.toString()}`);
    return response.data;
  },

  updateAlertThresholds: async (thresholds, locationId = null) => {
    const response = await axiosInstance.put('/alert-thresholds', {
      thresholds,
      locationId
    });
    return response.data;
  },

  // Equipment Configuration endpoints (for cross-org benchmarking)
  getWashManufacturers: async () => {
    const response = await axiosInstance.get('/catalogs/equipment');
    return response.data;
  },

  getEquipmentConfig: async (locationId) => {
    const response = await axiosInstance.get(`/locations/${locationId}/equipment`);
    return response.data;
  },

  updateEquipmentConfig: async (locationId, config) => {
    const response = await axiosInstance.put(`/locations/${locationId}/equipment`, config);
    return response.data;
  },

  getEquipmentHistory: async (locationId) => {
    const response = await axiosInstance.get(`/locations/${locationId}/equipment/history`);
    return response.data;
  },

  grantBenchmarkingConsent: async (locationId) => {
    const response = await axiosInstance.post(`/locations/${locationId}/benchmarking/consent`);
    return response.data;
  },

  revokeBenchmarkingConsent: async (locationId) => {
    const response = await axiosInstance.delete(`/locations/${locationId}/benchmarking/consent`);
    return response.data;
  },

  getBenchmarkingConsent: async (locationId) => {
    const response = await axiosInstance.get(`/locations/${locationId}/benchmarking/consent`);
    return response.data;
  },

  // Firmware Rollout Management (developer only)
  getRolloutSettings: async () => {
    const response = await axiosInstance.get('/admin/rollout');
    return response.data;
  },

  setRolloutMode: async (mode) => {
    const response = await axiosInstance.put('/admin/rollout/mode', { mode });
    return response.data;
  },

  allowChipForUpdate: async (chipId) => {
    const response = await axiosInstance.post(`/admin/rollout/allow/${chipId}`);
    return response.data;
  },

  removeChipFromAllowed: async (chipId) => {
    const response = await axiosInstance.delete(`/admin/rollout/allow/${chipId}`);
    return response.data;
  },

  setAllowedChips: async (chipIds) => {
    const response = await axiosInstance.put('/admin/rollout/allowed-chips', { allowed_chips: chipIds });
    return response.data;
  },

  getDeviceFirmwareStatus: async () => {
    const response = await axiosInstance.get('/admin/device-firmware');
    return response.data;
  },

  // Chip Diagnostics (developer only)
  getChipDiagnostics: async () => {
    const response = await axiosInstance.get('/admin/chip-diagnostics');
    return response.data;
  },

  getChipDiagnosticHistory: async (chipId, limit = 50) => {
    const response = await axiosInstance.get(
      `/admin/chip-diagnostics/${encodeURIComponent(chipId)}`,
      { params: { limit } }
    );
    return response.data;
  },

  // AI Digest
  getAiDigest: async (orgId, locationId = null) => {
    const params = new URLSearchParams();
    if (locationId) params.append('locationId', locationId);
    // A missing digest is a successful { digest: null }; failed reads must reach Retry.
    const response = await axiosInstance.get(`/ai-digest/latest/${orgId}?${params}`);
    return response.data;
  },

// ============================================================================
  // CATALOG ENDPOINTS - Injectors and Tips (Reference Data)
  // ============================================================================

  getInjectorCatalog: async () => {
    const response = await axiosInstance.get('/catalogs/injectors');
    return response.data;
  },

  getTipCatalog: async (injectorBrand = null) => {
    const params = injectorBrand ? { injectorBrand } : {};
    const response = await axiosInstance.get('/catalogs/tips', { params });
    return response.data;
  },

  upsertInjectorBrand: async (brandData) => {
    const response = await axiosInstance.post('/catalogs/injectors', brandData);
    return response.data;
  },

  upsertTipColor: async (tipData) => {
    const response = await axiosInstance.post('/catalogs/tips', tipData);
    return response.data;
  },

  deleteInjectorBrand: async (brandKey) => {
    const response = await axiosInstance.delete(`/catalogs/injectors/${brandKey}`);
    return response.data;
  },

  deleteTipColor: async (colorKey) => {
    const response = await axiosInstance.delete(`/catalogs/tips/${colorKey}`);
    return response.data;
  },

  seedCatalogs: async () => {
    const response = await axiosInstance.post('/catalogs/seed');
    return response.data;
  },

  // ============================================================================
  // EQUIPMENT CATALOG (Wash Manufacturers)
  // ============================================================================

  getEquipmentCatalog: async () => {
    const response = await axiosInstance.get('/catalogs/equipment');
    return response.data;
  },

  upsertWashManufacturer: async (manufacturerData) => {
    const response = await axiosInstance.post('/catalogs/equipment', manufacturerData);
    return response.data;
  },

  deleteWashManufacturer: async (id) => {
    const response = await axiosInstance.delete(`/catalogs/equipment/${id}`);
    return response.data;
  },

  // ============================================================================
  // CHEMICAL CATALOG (Vendors, Types, Products)
  // ============================================================================

  getChemicalVendorsCatalog: async () => {
    const response = await axiosInstance.get('/catalogs/chemical-vendors');
    return response.data;
  },

  upsertChemicalVendor: async (vendorData) => {
    const response = await axiosInstance.post('/catalogs/chemical-vendors', vendorData);
    return response.data;
  },

  deleteChemicalVendor: async (id) => {
    const response = await axiosInstance.delete(`/catalogs/chemical-vendors/${id}`);
    return response.data;
  },

  getChemicalTypesCatalog: async () => {
    const response = await axiosInstance.get('/catalogs/chemical-types');
    return response.data;
  },

  upsertChemicalType: async (typeData) => {
    const response = await axiosInstance.post('/catalogs/chemical-types', typeData);
    return response.data;
  },

  deleteChemicalType: async (id) => {
    const response = await axiosInstance.delete(`/catalogs/chemical-types/${id}`);
    return response.data;
  },

  getChemicalProductsCatalog: async (vendorId = null) => {
    const params = vendorId ? { vendorId } : {};
    const response = await axiosInstance.get('/catalogs/chemical-products', { params });
    return response.data;
  },

  upsertChemicalProduct: async (productData) => {
    const response = await axiosInstance.post('/catalogs/chemical-products', productData);
    return response.data;
  },

  deleteChemicalProduct: async (id) => {
    const response = await axiosInstance.delete(`/catalogs/chemical-products/${id}`);
    return response.data;
  },

  // SDS on catalog products: the PDF is uploaded to Firebase Storage by the caller
  // (sds/catalog/<productId>.pdf); this tells the API to screen it and attach it.
  uploadCatalogProductSds: async (id, { storagePath, sourceUrl, revisionDate, region, force } = {}) => {
    const response = await axiosInstance.post(`/catalogs/chemical-products/${id}/sds`, {
      storagePath, sourceUrl, revisionDate, region, force
    });
    return response.data;
  },

  // Tech sheet (TDS) on catalog products: the PDF is uploaded to Firebase Storage by the caller
  // (sds/catalog/tds/<productId>.pdf); this tells the API to attach it (no screening).
  uploadCatalogProductTds: async (id, { storagePath, sourceUrl } = {}) => {
    const response = await axiosInstance.post(`/catalogs/chemical-products/${id}/tds`, {
      storagePath, sourceUrl
    });
    return response.data;
  },

  // Customer-side request for a missing SDS or tech sheet (docType 'sds' | 'tds'; emails info@,
  // listed on Developer Console → SDS Library).
  requestSds: async ({ locationId, productId, productName, vendorId, vendorName, chemicalId, docType = 'sds' }) => {
    const response = await axiosInstance.post('/sds-requests', {
      locationId, productId, productName, vendorId, vendorName, chemicalId, docType
    });
    return response.data;
  },

  // Developer: open SDS requests (status 'open' | 'fulfilled' | 'all')
  getSdsRequests: async (status = 'open') => {
    const response = await axiosInstance.get('/sds-requests', { params: { status } });
    return response.data;
  },

  // ============================================================================
  // REFERENCE LIBRARY ENDPOINTS (SDS, Equipment, Injector, Compressor Manuals)
  // ============================================================================

  getReferenceLibrary: async (type = null, vendorId = null) => {
    const params = {};
    if (type) params.type = type;
    if (vendorId) params.vendorId = vendorId;
    const response = await axiosInstance.get('/reference-library', { params });
    return response.data;
  },

  getReferenceLibraryDoc: async (docId) => {
    const response = await axiosInstance.get(`/reference-library/${docId}`);
    return response.data;
  },

  getReferenceLibraryTypes: async () => {
    const response = await axiosInstance.get('/reference-library/types');
    return response.data;
  },

  searchReferenceLibrary: async (query, type = null) => {
    const params = { q: query };
    if (type) params.type = type;
    const response = await axiosInstance.get('/reference-library/search', { params });
    return response.data;
  },

  createReferenceLibraryDoc: async (data) => {
    const response = await axiosInstance.post('/reference-library', data);
    return response.data;
  },

  retryReferenceLibraryExtraction: async (docId) => {
    const response = await axiosInstance.post(`/reference-library/${docId}/extract`);
    return response.data;
  },

  updateReferenceLibraryDoc: async (docId, data) => {
    const response = await axiosInstance.put(`/reference-library/${docId}`, data);
    return response.data;
  },

  deleteReferenceLibraryDoc: async (docId) => {
    const response = await axiosInstance.delete(`/reference-library/${docId}`);
    return response.data;
  },

  // ============================================================================
  // SENSOR INJECTOR/TIP/TITRATION ENDPOINTS
  // ============================================================================

  updateSensorInjector: async (chipId, sensorId, injectorData) => {
    const response = await axiosInstance.put(`/sensors/${chipId}/${sensorId}/injector`, injectorData);
    return response.data;
  },


  updateSensorTip: async (chipId, sensorId, tipData) => {
    const response = await axiosInstance.put(`/sensors/${chipId}/${sensorId}/tip`, tipData);
    return response.data;
  },


  // ============================================================================
  // TITRATIONS (one record per physical test, any method; "flow-titration" URLs are historical)
  // ============================================================================

  /**
   * Record a titration result
   * @param {string} chipId - Chip ID
   * @param {string} sensorId - Sensor ID
   * @param {Object} data - { method, result_value, result_unit, target_min, target_max, measured_flow_rate, baseline_flow_rate, notes }
   */
  recordTitration: async (chipId, sensorId, data) => {
    const response = await axiosInstance.post(`/sensors/${chipId}/${sensorId}/flow-titration`, data);
    return response.data;
  },

  /**
   * Get titration history for a sensor
   * @param {string} chipId - Chip ID
   * @param {string} sensorId - Sensor ID
   * @param {Object} options - { limit, season }
   */
  getTitrationHistory: async (chipId, sensorId, options = {}) => {
    const response = await axiosInstance.get(`/sensors/${chipId}/${sensorId}/flow-titrations`, {
      params: {
        limit: options.limit || 50,
        season: options.season
      }
    });
    return response.data;
  },

  /**
   * Get baseline flow rate from Bigtable events
   * @param {string} chipId - Chip ID
   * @param {string} sensorId - Sensor ID
   * @param {number} days - Number of days to look back (default 30)
   */
  getBaselineFlowRate: async (chipId, sensorId, days = 30, sampleSize = 100) => {
    const response = await axiosInstance.get(`/sensors/${chipId}/${sensorId}/baseline-flow`, {
      params: { days, sampleSize }
    });
    return response.data;
  },

  /**
   * Get all sensors with tip info for Injector Tips page
   * @param {Object} options - { locationId }
   */
  getInjectorTipsSummary: async (options = {}) => {
    const response = await axiosInstance.get('/injector-tips', {
      params: {
        location_id: options.locationId
      }
    });
    return response.data;
  },

  // ============================================================================
  // SETUP WIZARD ENDPOINTS (Self-Service Onboarding)
  // See: docs/SELF-SERVICE-SETUP-WIZARD.md
  // ============================================================================

  /**
   * Get list of chemical vendors for dropdown
   */
  getChemicalVendors: async () => {
    const response = await axiosInstance.get('/catalogs/chemical-vendors');
    return response.data;
  },

  /**
   * Get products for a specific vendor
   */
  getVendorProducts: async (vendorId) => {
    if (vendorId === 'other') {
      return { items: [], message: 'Enter custom product name' };
    }
    const response = await axiosInstance.get('/catalogs/chemical-products', { params: { vendorId } });
    return response.data;
  },

  /**
   * Get list of standardized chemical types for benchmarking
   */
  getChemicalTypes: async () => {
    const response = await axiosInstance.get('/catalogs/chemical-types');
    return response.data;
  },

  /**
   * Get devices claimed by the user's organization
   * @param {Object} options - Optional parameters
   * @param {boolean} options.unassigned - If true, only return devices without a locationId
   */
  getClaimedDevices: async (options = {}) => {
    const params = {};
    if (options.unassigned) params.unassigned = 'true';
    const response = await axiosInstance.get('/setup/claimed-devices', { params });
    return response.data;
  },

  /**
   * Get a single organization by ID (for add-device wizard)
   */
  getOrganization: async (orgId) => {
    const response = await axiosInstance.get(`/setup/organizations/${orgId}`);
    return response.data;
  },

  /**
   * Get a single location by ID (for add-device wizard)
   */
  getLocation: async (locationId) => {
    const response = await axiosInstance.get(`/setup/locations/${locationId}`);
    return response.data;
  },

  /**
   * Get bay configuration for a location
   */
  getBays: async (locationId) => {
    const response = await axiosInstance.get(`/setup/bays/${locationId}`);
    return response.data;
  },

  /**
   * Get the setup status for the user's organization
   */
  getSetupStatus: async () => {
    const response = await axiosInstance.get('/setup/status');
    return response.data;
  },

  /**
   * Setup guide check marks for one location (docs/2026-10-23-SETUP-GUIDE-DESIGN.md)
   */
  getSetupProgress: async (locationId) => {
    const response = await axiosInstance.get('/setup/progress', { params: { locationId } });
    return response.data;
  },

  /**
   * First-time setup for Inventory and Barrel Configuration: preview and create
   * (one chemical per name, one barrel per line, for a location with none yet)
   */
  getFirstTimeInventory: async (locationId) => {
    const response = await axiosInstance.get('/setup/first-time-inventory', { params: { locationId } });
    return response.data;
  },
  createFirstTimeInventory: async ({ locationId, barrelSizeGallons, lineIds }) => {
    const response = await axiosInstance.post('/setup/first-time-inventory', { locationId, barrelSizeGallons, lineIds });
    return response.data;
  },

  /**
   * Turn the setup guide on or off for the signed-in user
   */
  /**
   * Setup guide step 4: tick (or untick) that Chronological Usage was checked at a location
   */
  setUsageChecked: async (locationId, checked) => {
    const response = await axiosInstance.post('/setup/usage-checked', { locationId, checked });
    return response.data;
  },

  setSetupGuidePreference: async (enabled) => {
    const response = await axiosInstance.put('/setup/guide-preference', { enabled });
    return response.data;
  },

  /**
   * Provision the system with wizard configuration
   * Performs atomic write of all configuration data
   */
  provisionSystem: async (config) => {
    const response = await axiosInstance.post('/setup/provision', config, {
      timeout: 60000 // 60 second timeout for provisioning
    });
    return response.data;
  },

  /**
   * Configure a device at an existing location (add-device wizard mode)
   * Updates sensor configs, assigns device to bay, adds new barrels/inventory
   */
  configureDevice: async (config) => {
    const response = await axiosInstance.post('/setup/configure-device', config, {
      timeout: 60000 // 60 second timeout
    });
    return response.data;
  },

  /**
   * Remove an orphaned device (only if all sensors are Unassigned)
   */
  removeDevice: async (hardwareId) => {
    const response = await axiosInstance.delete(`/setup/device/${hardwareId}`);
    return response.data;
  },

  /**
   * Create a location standalone (without the full wizard)
   */
  createLocation: async ({ name, address, timezone }) => {
    const response = await axiosInstance.post('/setup/locations', { name, address, timezone });
    return response.data;
  },

  /**
   * Copy Location Setup: boxes with channels set up at a location (to copy from)
   */
  getCopyLocationBoxes: async (locationId) => {
    const response = await axiosInstance.get('/setup/copy-location/boxes', { params: { locationId }, skipAutoLocation: true });
    return response.data;
  },

  /**
   * Copy Location Setup: what a copy would do (writes nothing).
   * body: { sourceLocationId, targetLocationId, pairs: [{ sourceChipId, targetChipId }], copyPrices, copyReportSchedules }
   */
  previewCopyLocation: async (body) => {
    const response = await axiosInstance.post('/setup/copy-location/preview', body, { skipAutoLocation: true });
    return response.data;
  },

  /**
   * Copy Location Setup: run the copy
   */
  copyLocation: async (body) => {
    const response = await axiosInstance.post('/setup/copy-location', body, { skipAutoLocation: true });
    return response.data;
  },

  /**
   * Assign an unassigned device to a location
   */
  assignDeviceToLocation: async (hardwareId, locationId) => {
    const response = await axiosInstance.put(`/setup/device/${hardwareId}/assign`, { locationId });
    return response.data;
  },

  // Setup namespace for cleaner imports
  setup: {
    getVendors: async () => {
      const response = await axiosInstance.get('/catalogs/chemical-vendors');
      return response.data;
    },
    getVendorProducts: async (vendorId) => {
      if (vendorId === 'other') {
        return { items: [], message: 'Enter custom product name' };
      }
      const response = await axiosInstance.get('/catalogs/chemical-products', { params: { vendorId } });
      return response.data;
    },
    getChemicalTypes: async () => {
      const response = await axiosInstance.get('/catalogs/chemical-types');
      return response.data;
    },
    getClaimedDevices: async (options = {}) => {
      const params = {};
      if (options.unassigned) params.unassigned = 'true';
      const response = await axiosInstance.get('/setup/claimed-devices', { params });
      return response.data;
    },
    getStatus: async () => {
      const response = await axiosInstance.get('/setup/status');
      return response.data;
    },
    provision: async (config) => {
      const response = await axiosInstance.post('/setup/provision', config, {
        timeout: 60000
      });
      return response.data;
    }
  },

  // Demo wash replay status (developer only)
  simulator: {
    getStatus: async () => {
      const response = await axiosInstance.get('/developer/simulator/status');
      return response.data;
    }
  }
};
